/**
 * useOrderData — Fetches and processes order data for the card-payment confirmation page.
 * Extracted from the inline logic previously in order-page.tsx.
 */
import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { CHECKOUT_SESSION, PAYMENT_INTENT, PAYMENT_CREATE } from '../../../shared/constants/api';
import * as api from '../../../shared/services/apiClient';
import type { Order } from '../../../shared/types/models';
import { getProductById, getCategoryById } from '../../cart/utils/cartUtil';
import { PASS_PRODUCT_TYPE_ID, ADDON_PRODUCT_TYPE_ID, MIN_LOAD_TIME, PAYMENT_METHOD, ORDER_TEXTS, ORDER_FIELD_KEYS, LOCALE_EN, ORDER_STORAGE_KEYS, ORDER_PARAM_KEYS, ORDER_ATTRIBUTE_KEYS, ORDER_CT_ACTIONS, ORDER_CT_TYPES, ORDER_DATE_OPTIONS } from '../constants/order.constants';
import { ensureOrderExists, getOrderById, updateOrderCustomFields } from '../services/order.service';
import type {
    OrderWithPayment,
    DisplayProduct,
    NextStep,
    HotelMapInfo,
    OrderLineItem,
    AssociationGroup,
    UseOrderDataReturn,
} from '../types/order.types';


const formatDate = (
    dateString: string | undefined | null,
    options: Intl.DateTimeFormatOptions = ORDER_DATE_OPTIONS
): string => {
    if (!dateString) return '';
    try {
        return new Date(dateString).toLocaleDateString(LOCALE_EN, options);
    } catch (err) {
        console.error('Failed to format date:', err);
        return String(dateString);
    }
};

/**
 * Hook that fetches order data for card-payment confirmation.
 * Handles Stripe session/payment-intent flows and stored order IDs.
 */
export const useOrderData = (): UseOrderDataReturn => {
    const [searchParams] = useSearchParams();

    const [order, setOrder] = useState<OrderWithPayment | null>(null);
    const [products, setProducts] = useState<DisplayProduct[]>([]);
    const [customerEmail, setCustomerEmail] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [nextSteps, setNextSteps] = useState<NextStep[]>([]);
    const [hotelMapInfo, setHotelMapInfo] = useState<HotelMapInfo | null>(null);

    const sessionId = searchParams.get(ORDER_PARAM_KEYS.SESSION_ID);
    const paymentIntentId = searchParams.get(ORDER_PARAM_KEYS.PAYMENT_INTENT);
    const storedOrderId = sessionStorage.getItem(ORDER_STORAGE_KEYS.ORDER_ID);

    const processedRef = useRef(false);
    const lastParamsRef = useRef<string | null>(null);

    useEffect(() => {
        const paramsKey = `${sessionId ?? ''}|${paymentIntentId ?? ''}|${storedOrderId ?? ''}`;
        if (lastParamsRef.current !== paramsKey) {
            lastParamsRef.current = paramsKey;
            processedRef.current = false;
        }

        const fetchOrderData = async () => {
            if (processedRef.current) return;
            processedRef.current = true;

            const startTime = Date.now();

            try {
                let currentOrder: OrderWithPayment | null = null;
                let cartId: string | null = null;

                if (sessionId || paymentIntentId) {
                    let cartVersion: string | number | null = null;

                    if (sessionId) {
                        const res = await api.get(CHECKOUT_SESSION(sessionId));
                        if (!res.ok) throw new Error('Failed to retrieve session');
                        const data = (await res.json()) as { metadata?: { cartId?: string; cartVersion?: string | number } };
                        cartId = data.metadata?.cartId ?? null;
                        cartVersion = data.metadata?.cartVersion ?? null;
                    } else if (paymentIntentId) {
                        const res = await api.get(PAYMENT_INTENT(paymentIntentId));
                        if (!res.ok) throw new Error('Failed to retrieve payment intent');
                        const data = (await res.json()) as { metadata?: { cartId?: string; cartVersion?: string | number } };
                        cartId = data.metadata?.cartId ?? null;
                        cartVersion = data.metadata?.cartVersion ?? null;
                    }

                    if (cartId) {
                        // 1. Fetch or create order, but defer the custom field update to consolidate with payment
                        currentOrder = (await ensureOrderExists(cartId, cartVersion, [], true)) as OrderWithPayment | null;

                        if (currentOrder && paymentIntentId) {
                            const payments = currentOrder.paymentInfo?.payments;
                            const hasPayment = Array.isArray(payments) && payments.length > 0;

                            if (!hasPayment) {
                                try {
                                    // 2. Resolve accurate amount from the actual Order object (including tax)
                                    const amountCent =
                                        currentOrder.taxedPrice?.totalGross?.centAmount ||
                                        currentOrder.totalPrice?.centAmount ||
                                        0;
                                    const currencyCode =
                                        currentOrder.taxedPrice?.totalGross?.currencyCode ||
                                        currentOrder.totalPrice?.currencyCode ||
                                        ORDER_FIELD_KEYS.CURRENCY_AUTO;

                                    const paymentDraft = {
                                        amountPlanned: { currencyCode, centAmount: amountCent },
                                        paymentMethodInfo: { paymentInterface: PAYMENT_METHOD.STRIPE, method: PAYMENT_METHOD.CARD },
                                        interfaceId: paymentIntentId,
                                    };

                                    // 3. Create the payment
                                    const paymentRes = await api.post(PAYMENT_CREATE, paymentDraft);
                                    if (paymentRes.ok) {
                                        const payment = await paymentRes.json();
                                        
                                        // 4. Perform the ONE and ONLY update call with both Amber fields and Payment
                                        currentOrder = (await updateOrderCustomFields(currentOrder as unknown as Record<string, unknown>, [
                                            {
                                                action: ORDER_CT_ACTIONS.ADD_PAYMENT,
                                                payment: { typeId: ORDER_CT_TYPES.PAYMENT, id: payment.id }
                                            }
                                        ])) as unknown as OrderWithPayment;
                                    }
                                } catch (payErr) {
                                    console.error('Failed to process consolidated card payment:', payErr);
                                }
                            }
                        } else if (currentOrder) {
                             // If no payment needed, still do the custom field update once
                             currentOrder = (await updateOrderCustomFields(currentOrder as unknown as Record<string, unknown>)) as unknown as OrderWithPayment;
                        }
                    }
                } else if (storedOrderId) {
                    currentOrder = (await getOrderById(storedOrderId)) as OrderWithPayment | null;
                }

                if (currentOrder) {
                    setOrder(currentOrder);
                    setCustomerEmail(currentOrder.customerEmail ?? '');

                    sessionStorage.removeItem(ORDER_STORAGE_KEYS.CART_ID);
                    localStorage.removeItem(ORDER_STORAGE_KEYS.CART_ID);
                    sessionStorage.removeItem(ORDER_STORAGE_KEYS.PRODUCT_STORE_MAP);
                    import('../../cart/utils/cartUtil').then(({ emitCartUpdate }) => {
                        if (emitCartUpdate) emitCartUpdate(null);
                    });

                    const { processedProducts, configProductId, hotelMapInfo: hotelInfo } = await processLineItems(currentOrder);
                    setProducts(processedProducts);
                    if (hotelInfo) setHotelMapInfo(hotelInfo);

                    if (configProductId) {
                        const steps = await fetchNextSteps(configProductId, ORDER_ATTRIBUTE_KEYS.CARD_NEXT_STEPS);
                        if (steps.length > 0) setNextSteps(steps);
                    }
                }
            } catch (err) {
                console.error('Failed to load order data:', err);
                setError(ORDER_TEXTS.ERRORS.FETCH_FAILED);
            } finally {
                const elapsedTime = Date.now() - startTime;
                const remainingTime = Math.max(0, MIN_LOAD_TIME - elapsedTime);
                setTimeout(() => setLoading(false), remainingTime);
            }
        };

        fetchOrderData();
    }, [sessionId, paymentIntentId, storedOrderId]);

    return { order, products, customerEmail, nextSteps, hotelMapInfo, loading, error };
};


// ─── Shared Helpers ─────────────────────────────────────────────────

/**
 * Processes line items from an order into DisplayProduct[] for confirmation UI.
 */
export const processLineItems = async (
    currentOrder: Order & { lineItems?: OrderLineItem[] }
): Promise<{ processedProducts: DisplayProduct[]; configProductId: string | null; hotelMapInfo: HotelMapInfo | null }> => {
    const associationGroups: Record<string, AssociationGroup> = {};
    const lineItems = currentOrder.lineItems ?? [];

    lineItems.forEach((item: OrderLineItem) => {
        const assocKey = item.custom?.fields?.[ORDER_FIELD_KEYS.ASSOCIATION_KEY] as string | undefined;
        if (assocKey) {
            if (!associationGroups[assocKey]) {
                associationGroups[assocKey] = { passes: [], addons: [] };
            }
            const productTypeId = item.productType?.id;
            if (productTypeId === PASS_PRODUCT_TYPE_ID) {
                associationGroups[assocKey].passes.push(item);
            } else if (productTypeId === ADDON_PRODUCT_TYPE_ID) {
                associationGroups[assocKey].addons.push(item);
            }
        }
    });

    let configurationProductId: string | null = null;
    let hotelMapInfo: HotelMapInfo | null = null;
    const productsList = await Promise.all(
        Object.entries(associationGroups).map(async ([, group]) => {
            const firstPass = group.passes[0];
            if (!firstPass) return null;

            const productData = await getProductById(firstPass.productId);
            const totalPassQuantity = group.passes.reduce((sum, pass) => sum + (pass.quantity ?? 1), 0);

            let unitPassPrice = 0;
            let unitOriginalPrice = 0;

            if (firstPass.price) {
                if (firstPass.price.discounted) {
                    unitPassPrice = firstPass.price.discounted.value?.centAmount ?? 0;
                    unitOriginalPrice = firstPass.price.value?.centAmount ?? 0;
                } else {
                    unitPassPrice = firstPass.price.value?.centAmount ?? 0;
                    unitOriginalPrice = 0;
                }
            }

            let productName: string = ORDER_TEXTS.UNKNOWN_PRODUCT;
            let productLocation: string | null = null;
            let productDate: string | null = null;
            let startDate: string | null = null;
            let endDate: string | null = null;

            if (productData) {
                interface CommercetoolsProduct {
                    name?: Record<string, string>;
                    attributes?: Array<{ name: string; value?: unknown }>;
                    masterVariant?: {
                        attributes?: Array<{ name: string; value?: unknown }>;
                        prices?: Array<{ value: { centAmount: number; currencyCode: string } }>;
                    };
                    variants?: Array<{
                        id: number;
                        attributes?: Array<{ name: string; value?: unknown }>;
                        prices?: Array<{ value: { centAmount: number; currencyCode: string } }>;
                    }>;
                    categories?: Array<{ id: string }>;
                }
                const productRec = productData as unknown as {
                    masterData?: {
                        current?: CommercetoolsProduct;
                        staged?: CommercetoolsProduct;
                    }
                };
                const currentData = (productRec.masterData?.current || productRec.masterData?.staged) as CommercetoolsProduct | undefined;

                const nameObj = currentData?.name as Record<string, string> | undefined;
                if (nameObj) {
                    productName = nameObj[LOCALE_EN] || (Object.values(nameObj)[0] as string);
                }

                if (unitOriginalPrice === 0 || unitOriginalPrice <= unitPassPrice) {
                    const allVariants = [currentData?.masterVariant, ...(currentData?.variants || [])].filter(Boolean);
                    const variant = (allVariants.find(
                        (v) => (v as { id?: number })?.id === firstPass.variant?.id
                    ) || currentData?.masterVariant) as { prices?: Array<{ value: { currencyCode: string; centAmount?: number } }> };

                    if (variant?.prices) {
                        const curr = firstPass.price?.value?.currencyCode || ORDER_FIELD_KEYS.CURRENCY_AUTO;
                        const prices = (variant.prices as Array<{ value: { currencyCode: string; centAmount?: number } }>).filter((p) => p.value.currencyCode === curr);
                        if (prices.length > 0) {
                            prices.sort((a, b) => (b.value.centAmount ?? 0) - (a.value.centAmount ?? 0));
                            const highestPrice = prices[0].value.centAmount ?? 0;
                            if (highestPrice > unitPassPrice) unitOriginalPrice = highestPrice;
                        }
                    }
                }

                const categories = currentData?.categories as Array<{ id: string }> | undefined;
                if (categories?.length) {
                    const categoryData = await getCategoryById(categories[0].id);

                    const categoryFields = (categoryData as unknown as Record<string, unknown>)?.custom as
                        | { fields?: Record<string, unknown> }
                        | undefined;

                    // ── Hotel map: extract once from first resolved category ──
                    if (!hotelMapInfo && categoryFields?.fields) {
                        const status = categoryFields.fields[ORDER_FIELD_KEYS.HOTEL_MAP_STATUS];
                        const url = categoryFields.fields[ORDER_FIELD_KEYS.HOTEL_MAP_URL];
                        if (typeof status === 'string' && typeof url === 'string') {
                            hotelMapInfo = { status, url };
                        }
                    }

                    const confProduct = categoryFields as { fields?: { [ORDER_FIELD_KEYS.CONFIG_PRODUCT]?: { id?: string } } } | undefined;
                    if (!configurationProductId && confProduct?.fields?.[ORDER_FIELD_KEYS.CONFIG_PRODUCT]?.id) {
                        configurationProductId = confProduct.fields[ORDER_FIELD_KEYS.CONFIG_PRODUCT]!.id!;
                    }

                    const configurationProductRef = (categoryData as { custom?: { fields?: { [ORDER_FIELD_KEYS.CONFIG_PRODUCT]?: { id?: string; obj?: unknown } } } })?.custom?.fields?.[ORDER_FIELD_KEYS.CONFIG_PRODUCT];
                    if (configurationProductRef) {
                        let configProduct = configurationProductRef.obj as { masterData?: { current?: { masterVariant?: { attributes?: Array<{ name: string; value?: unknown }> } }; staged?: { masterVariant?: { attributes?: Array<{ name: string; value?: unknown }> } } } } | null;
                        if (!configProduct) {
                            configProduct = await getProductById(configurationProductRef.id!) as unknown as { masterData?: { current?: { masterVariant?: { attributes?: Array<{ name: string; value?: unknown }> } }; staged?: { masterVariant?: { attributes?: Array<{ name: string; value?: unknown }> } } } };
                        }
                        if (configProduct) {
                            const configAttributes =
                                configProduct.masterData?.current?.masterVariant?.attributes ||
                                configProduct.masterData?.staged?.masterVariant?.attributes ||
                                [];
                            const eventVenue = (configAttributes as Array<{ name: string; value: unknown }>).find((a) => a.name === ORDER_FIELD_KEYS.EVENT_VENUE)?.value as string | undefined;
                            const eventLocation = (configAttributes as Array<{ name: string; value: unknown }>).find((a) => a.name === ORDER_FIELD_KEYS.EVENT_LOCATION)?.value as string | undefined;
                            if (eventVenue && eventLocation) productLocation = `${eventVenue}, ${eventLocation}`;
                            else productLocation = eventVenue || eventLocation || null;
                        }
                    } else {
                        const venue = ((categoryData as unknown as Record<string, unknown>)?.custom as { fields?: { [ORDER_FIELD_KEYS.VENUE]?: string } })?.fields?.[ORDER_FIELD_KEYS.VENUE];
                        if (venue) productLocation = venue;
                    }
                }

                let attributes: Array<{ name: string; value?: unknown }> =
                    currentData?.masterVariant?.attributes || currentData?.attributes || [];
                const allVars = [currentData?.masterVariant, ...(currentData?.variants ?? [])];
                const match = allVars.find((v) => (v as { id?: number })?.id === firstPass.variant?.id);
                if (match) attributes = (match as { attributes?: Array<{ name: string; value?: unknown }> }).attributes ?? [];
                if (firstPass.variant?.attributes) attributes = firstPass.variant.attributes;

                startDate = (attributes.find((a) => a.name === ORDER_FIELD_KEYS.START_DATE)?.value as string) ?? null;
                endDate = (attributes.find((a) => a.name === ORDER_FIELD_KEYS.END_DATE)?.value as string) ?? null;
                if (startDate && endDate) productDate = `${formatDate(startDate)} – ${formatDate(endDate)}`;
                else if (startDate) productDate = formatDate(startDate);
            }

            return {
                id: firstPass.id,
                name: productName,
                price: unitPassPrice,
                originalPrice: unitOriginalPrice > unitPassPrice ? unitOriginalPrice : null,
                date: productDate,
                startDate,
                endDate,
                location: productLocation,
                addOnsCount: group.addons.length,
                quantity: totalPassQuantity,
            } as DisplayProduct;
        })
    );

    return {
        processedProducts: productsList.filter((p): p is DisplayProduct => p != null),
        configProductId: configurationProductId,
        hotelMapInfo,
    };
};


/**
 * Fetches next-step items from a configuration product's attributes.
 */
export const fetchNextSteps = async (
    configProductId: string,
    attributeName: string
): Promise<NextStep[]> => {
    try {
        const product = await getProductById(configProductId);
        if (!product) return [];

        const current = (product as unknown as { masterData?: { current?: { masterVariant?: { attributes?: Array<{ name: string; value?: unknown }> } }; staged?: { masterVariant?: { attributes?: Array<{ name: string; value?: unknown }> } } } }).masterData?.current || (product as unknown as { masterData?: { current?: { masterVariant?: { attributes?: Array<{ name: string; value?: unknown }> } }; staged?: { masterVariant?: { attributes?: Array<{ name: string; value?: unknown }> } } } }).masterData?.staged;
        const attributes = current?.masterVariant?.attributes ?? [];
        const stepsAttr = (attributes as Array<{ name: string; value?: unknown }>).find((a) => a.name === attributeName);
        const stepsValue = stepsAttr?.value;

        if (stepsValue && Array.isArray(stepsValue)) {
            const getLocText = (val: unknown): string => {
                if (!val) return '';
                if (typeof val === 'string') return val;
                const v = val as Record<string, string>;
                return v[LOCALE_EN] || (Object.values(v)[0] as string) || '';
            };
            const formattedSteps = (stepsValue as Array<Array<{ name: string; value?: unknown }>>).map((stepAttrs) => {
                const titleAttr = stepAttrs.find((x) => x.name === ORDER_FIELD_KEYS.STEP_TITLE);
                const textAttr = stepAttrs.find((x) => x.name === ORDER_FIELD_KEYS.STEP_DESCRIPTION);
                return {
                    title: getLocText(titleAttr?.value),
                    description: getLocText(textAttr?.value),
                    icon: 'file',
                };
            });
            return formattedSteps;
        }
    } catch (err) {
        console.error('Failed to fetch next steps:', err);
    }
    return [];
};

